import React, { useState, useEffect } from 'react';
import {
  Pane,
  Button,
  Dialog,
  TextInputField,
  toaster,
  Spinner,
  Table,
  Text,
  IconButton,
  PlusIcon,
  TrashIcon,
  Dialog as DeleteDialog
} from 'evergreen-ui';
import api from '../services/api';
import { useProject } from '../context/ProjectContext';

function Projects() {
  const { selectProject, currentProject } = useProject();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch projects on component mount
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
      setIsLoading(false);
    } catch (error) {
      toaster.danger('Failed to load projects');
      setIsLoading(false);
    }
  };

  const handleCreateProject = async () => {
    try {
      setIsSubmitting(true);
      const response = await api.post('/projects', {
        name: newProjectName
      });
      
      setProjects([...projects, response.data]);
      toaster.success('Project created successfully');
      setShowNewProjectDialog(false);
      setNewProjectName('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create project';
      toaster.danger(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateProjectName = (name) => {
    const regex = /^[a-zA-Z0-9_-]+$/;
    if (!name) return "Project name is required";
    if (name.length > 20) return "Project name must be 20 characters or less";
    if (!regex.test(name)) return "Project name can only contain letters, numbers, underscores, and hyphens";
    return null;
  };

  const handleProjectSelect = (project) => {
    selectProject(project);
    // You could navigate to a specific view here
    // navigate(`/projects/${project.id}`);
  };

  const handleDeleteClick = (e, project) => {
    e.stopPropagation(); // Prevent row selection when clicking delete
    setProjectToDelete(project);
  };

  const handleDeleteProject = async () => {
    try {
      setIsDeleting(true);
      await api.delete(`/projects/${projectToDelete.id}`);
      
      // Remove project from state
      setProjects(projects.filter(p => p.id !== projectToDelete.id));
      
      // If deleted project was selected, clear selection
      if (currentProject?.id === projectToDelete.id) {
        selectProject(null);
      }
      
      toaster.success('Project deleted successfully');
      setProjectToDelete(null);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete project';
      toaster.danger(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height="100vh">
        <Spinner />
      </Pane>
    );
  }

  return (
    <Pane padding={16}>
      <Pane 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between" 
        marginBottom={16}
      >
        <Text size={600}>Projects</Text>
        <Button
          appearance="primary"
          iconBefore={PlusIcon}
          onClick={() => setShowNewProjectDialog(true)}
        >
          New Project
        </Button>
      </Pane>

      {projects.length === 0 ? (
        <Pane
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={200}
          background="tint2"
          borderRadius={4}
        >
          <Text size={500} color="muted">No projects yet</Text>
        </Pane>
      ) : (
        <Table>
          <Table.Head>
            <Table.TextHeaderCell>Project Name</Table.TextHeaderCell>
            <Table.TextHeaderCell>ID</Table.TextHeaderCell>
            <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {projects.map((project) => (
              <Table.Row 
                key={project.id}
                isSelectable
                isSelected={currentProject?.id === project.id}
                onSelect={() => handleProjectSelect(project)}
              >
                <Table.TextCell>{project.name}</Table.TextCell>
                <Table.TextCell>{project.id}</Table.TextCell>
                <Table.Cell>
                  <IconButton
                    icon={TrashIcon}
                    intent="danger"
                    onClick={(e) => handleDeleteClick(e, project)}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <Dialog
        isShown={showNewProjectDialog}
        title="Create New Project"
        onCloseComplete={() => {
          setShowNewProjectDialog(false);
          setNewProjectName('');
        }}
        confirmLabel="Create"
        isConfirmLoading={isSubmitting}
        onConfirm={handleCreateProject}
        shouldCloseOnOverlayClick={false}
      >
        <TextInputField
          label="Project Name"
          placeholder="Enter project name"
          value={newProjectName}
          onChange={e => setNewProjectName(e.target.value)}
          validationMessage={validateProjectName(newProjectName)}
          disabled={isSubmitting}
          required
          description="Use only letters, numbers, underscores, and hyphens. Maximum 20 characters."
        />
      </Dialog>

      <DeleteDialog
        isShown={!!projectToDelete}
        title="Delete Project"
        intent="danger"
        onCloseComplete={() => setProjectToDelete(null)}
        confirmLabel="Delete"
        isConfirmLoading={isDeleting}
        onConfirm={handleDeleteProject}
      >
        <Pane>
          <Text>
            Are you sure you want to delete project "{projectToDelete?.name}"? 
            This action cannot be undone.
          </Text>
        </Pane>
      </DeleteDialog>
    </Pane>
  );
}

export default Projects; 